import type {
  AIBuildAgentLoopResult,
  AIBuildLinkedProposal,
  AIBuildOrchestrationResult,
  AIBuildRepairableArtifactKind,
} from '../../../services/aiBuildService'
import type { AIBuildAdoptionTarget } from '../../../services/aiBuildAdoptionHandoff'
import {
  asRecordArray,
  firstArrayAt,
  firstRecordAt,
  isRecord,
  valueAtPath,
} from '../../../services/recordPathUtils'

export { firstArrayAt, firstRecordAt, isRecord, valueAtPath }
export const asArray = asRecordArray

export const isOrchestrationResult = (
  value: unknown
): value is AIBuildOrchestrationResult =>
  isRecord(value) && Array.isArray(value.linkedProposals)

export const aiBuildSessionKey = (projectId?: string | null) =>
  `infergraph.aiBuild.session.v1.${projectId || 'global'}`

export const readAIBuildPageSession = <T extends object>(
  projectId?: string | null
): Partial<T> | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(aiBuildSessionKey(projectId))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return isRecord(parsed) ? (parsed as Partial<T>) : null
  } catch {
    return null
  }
}

export const writeAIBuildPageSession = (
  projectId: string | null | undefined,
  state: unknown
) => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(
      aiBuildSessionKey(projectId),
      JSON.stringify(state)
    )
  } catch {
    // Session persistence is only a convenience for cross-editor handoff.
  }
}

// ---------------------------------------------------------------------------
// R017 E1 — AI Build 자동 프로젝트명.
// ---------------------------------------------------------------------------

const PROJECT_NAME_MAX = 40

// intent 전문 대신 앞부분 명사구 요약. LLM 호출 없이:
//   1) 첫 줄만 취한다
//   2) 첫 문장 구분자 (./!/?/…) 앞까지 취한다
//   3) 한국어 서술 종결부 ("~해줘", "~하고 싶어" 등) 는 잘라낸다
//   4) 40자 초과 시 마지막 공백 경계에서 자르고 "…" 를 붙인다
export const summarizeIntentAsProjectName = (rawIntent: string): string => {
  const firstLine = (rawIntent || '').split('\n')[0]?.trim() ?? ''
  if (!firstLine) return ''
  let phrase = firstLine.split(/(?<=[.!?。])\s/)[0] ?? firstLine
  // 흔한 요청 종결 표현 제거 → 남는 앞부분이 명사구에 가깝다.
  phrase = phrase
    .replace(
      /(을|를|이|가)?\s*(자동화|구축|생성|분석|관리)?\s*(해\s?줘|해\s?주세요|하고\s?싶(어|다|습니다)|만들(어\s?줘|고\s?싶어)|해야\s?(해|한다)|필요(해|하다))[.…!?\s]*$/u,
      (match, _p1, keep) => (keep ? ` ${keep}` : '')
    )
    .replace(/\s+/g, ' ')
    .trim()
  if (!phrase) phrase = firstLine
  if (phrase.length <= PROJECT_NAME_MAX) return phrase
  const cut = phrase.slice(0, PROJECT_NAME_MAX)
  const lastSpace = cut.lastIndexOf(' ')
  return `${(lastSpace > PROJECT_NAME_MAX / 2 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`
}

// 플래너 산출물에서 프로젝트명으로 쓸 title 을 찾는다. 우선순위:
// report title → workflow name → ontology 라벨. pack-grounded 응답도 같은
// draftArtifact/proposalBundle 컨테이너를 쓰므로 동일 경로로 커버된다.
export const plannerProposalTitleFrom = (
  result: AIBuildOrchestrationResult
): string | null => {
  const titleOf = (
    proposal: AIBuildLinkedProposal | undefined,
    keys: string[]
  ): string | null => {
    if (!proposal || !isRecord(proposal.response)) return null
    for (const container of ['draftArtifact', 'draft', 'proposalBundle']) {
      const bundle = (proposal.response as Record<string, unknown>)[container]
      if (!isRecord(bundle)) continue
      for (const key of keys) {
        const value = bundle[key]
        if (typeof value === 'string' && value.trim()) return value.trim()
      }
    }
    return null
  }
  const byKind = (kind: AIBuildLinkedProposal['kind']) =>
    result.linkedProposals.find((proposal) => proposal.kind === kind)
  const candidate =
    titleOf(byKind('report'), ['title', 'name']) ??
    titleOf(byKind('workflow'), ['name', 'title', 'label']) ??
    titleOf(byKind('ontology_capture'), ['title', 'name', 'label'])
  if (!candidate) return null
  return candidate.length > PROJECT_NAME_MAX
    ? `${candidate.slice(0, PROJECT_NAME_MAX).trimEnd()}…`
    : candidate
}

export const lifecycleValidationIssues = (
  validation?: Record<string, unknown> | null
) =>
  asArray(
    firstRecordAt(validation ?? {}, ['validationResult'])?.errors ??
      validation?.errors
  )

export const approvalRequiredRoles = (
  approval?: Record<string, unknown> | null,
  publish?: Record<string, unknown> | null
) => {
  const fromApproval = Array.isArray(approval?.requiredRoles)
    ? approval.requiredRoles
    : []
  if (fromApproval.length > 0) return fromApproval.map(String)
  const issue = lifecycleValidationIssues(publish).find((item) =>
    String(item.code ?? '').includes('approval_policy.denied')
  )
  const message = String(issue?.message ?? '')
  const match = message.match(/Required approver roles:\s*([^.]*)/i)
  if (!match?.[1]) return []
  return match[1]
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

export const textFrom = (item: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = item[key]
    if (typeof value === 'string' && value.trim()) return value
    if (typeof value === 'number') return String(value)
  }
  return 'Unnamed'
}

export const collectStringRefs = (
  value: unknown,
  keyPattern: RegExp,
  refs = new Set<string>(),
  depth = 0
) => {
  if (value == null || depth > 5) return refs
  if (typeof value === 'string') return refs
  if (Array.isArray(value)) {
    for (const item of value)
      collectStringRefs(item, keyPattern, refs, depth + 1)
    return refs
  }
  if (!isRecord(value)) return refs

  for (const [key, nestedValue] of Object.entries(value)) {
    if (keyPattern.test(key)) {
      collectStringRefsAtKey(nestedValue, refs)
    }
    collectStringRefs(nestedValue, keyPattern, refs, depth + 1)
  }
  return refs
}

const collectStringRefsAtKey = (value: unknown, refs: Set<string>) => {
  if (typeof value === 'string') refs.add(value)
  if (!Array.isArray(value)) return
  for (const item of value) {
    if (typeof item === 'string') refs.add(item)
    else if (isRecord(item)) {
      const id =
        item.refId ??
        item.id ??
        item.artifactId ??
        item.catalogRef ??
        item.label
      if (typeof id === 'string') refs.add(id)
    }
  }
}

export const errorMessageFrom = (value: unknown) => {
  if (value instanceof Error) return value.message
  if (isRecord(value)) {
    const message = value.message ?? value.detail ?? value.code
    if (typeof message === 'string') return message
  }
  if (typeof value === 'string') return value
  return 'Planner orchestration failed.'
}

export const agentRunIdFrom = (result: AIBuildAgentLoopResult | null) =>
  result?.runId ?? result?.run_id ?? null

export const adoptionTargetForKind = (
  kind: AIBuildLinkedProposal['kind']
): AIBuildAdoptionTarget | null => {
  if (kind === 'workflow') return 'workflow'
  if (kind === 'ontology_capture') return 'ontology'
  if (kind === 'report') return 'report'
  return null
}

/**
 * 채택 후 이동 라우트 (A7 — 실존 라우트 + 실제 파라미터 계약).
 *
 * * Designer: ``/designer/unified-designer`` — ``ids.workflowId`` 가 있으면
 *   ``workflow=`` 파라미터로 캔버스를 바로 연다 (UnifiedDesignerPage 계약).
 * * Report: ``/auto-insight/bi-report-builder`` — ``ids.reportId`` 가 있으면
 *   ``edit=`` 파라미터로 채택 리포트를 빌더에 연다 (ConsoleSurface 는
 *   ``edit``/``view`` 를 읽는다 — ``reportId`` 파라미터는 계약에 없음).
 */
export const adoptionRouteForTarget = (
  target: AIBuildAdoptionTarget,
  projectId?: string | null,
  ids?: { workflowId?: string | null; reportId?: string | null }
) => {
  const query = new URLSearchParams()
  if (projectId) query.set('projectId', projectId)
  if (target === 'workflow' || target === 'ontology') {
    if (ids?.workflowId) query.set('workflow', ids.workflowId)
    const params = query.toString()
    return `/designer/unified-designer${params ? `?${params}` : ''}`
  }
  if (ids?.reportId) query.set('edit', ids.reportId)
  const params = query.toString()
  return `/auto-insight/bi-report-builder${params ? `?${params}` : ''}`
}

export const lifecycleKindForProposal = (
  kind: AIBuildLinkedProposal['kind']
): 'workflow' | 'ontology' | 'report' | 'ready_made_pack' | null => {
  if (kind === 'workflow') return 'workflow'
  if (kind === 'ontology_capture') return 'ontology'
  if (kind === 'report') return 'report'
  if (kind === 'ready_made_pack') return 'ready_made_pack'
  return null
}

export const repairArtifactKindForProposal = (
  kind: AIBuildLinkedProposal['kind']
): AIBuildRepairableArtifactKind | null => {
  if (kind === 'workflow') return 'workflow'
  if (kind === 'ontology_capture') return 'ontology'
  if (kind === 'report') return 'report'
  return null
}

export const validationIssuesFromProposal = (proposal: AIBuildLinkedProposal) =>
  lifecycleValidationIssues(proposal.response)

export const proposalHasAssistantRepairAction = (
  proposal: AIBuildLinkedProposal
) =>
  asArray(proposal.response.nextActions).some(
    (action) =>
      action.kind === 'assistant.repair' ||
      action.endpoint === '/api/authoring/assistant/repair'
  )

export const proposalIsRepairable = (proposal: AIBuildLinkedProposal) =>
  Boolean(repairArtifactKindForProposal(proposal.kind)) &&
  (proposal.status === 'needs_repair' ||
    validationIssuesFromProposal(proposal).length > 0 ||
    proposalHasAssistantRepairAction(proposal))

export const repairVersionLabelFrom = (version: number) => `repair v${version}`
